import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { GridRowId, useGridApiContext } from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';

import { IDepartmentDTO, IHierarchicalDepartmentDTO } from 'src/api/dtos';
import { DepartmentPopover } from 'src/features/departments';
import { ChevronDownIcon } from 'src/shared/components';
import { formatDepartmentDisplay } from 'src/shared/utils';
import { getEmployeeDepartmentDisplayFormat } from 'src/store/user/user.selectors';

interface Props {
  id: GridRowId;
  field: string;
  department: IDepartmentDTO | null;
  onDepartmentChange: (department: IDepartmentDTO | null) => void;
}

export const TimesheetDepartmentEditCell: FC<Props> = ({ id, field, department, onDepartmentChange }) => {
  const theme = useTheme();
  const apiRef = useGridApiContext();
  const employeeDepartmentDisplayFormat = useSelector(getEmployeeDepartmentDisplayFormat);

  const ref = useRef(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const label = department
    ? formatDepartmentDisplay(department.description, department.number, employeeDepartmentDisplayFormat)
    : undefined;
  const open = Boolean(anchorEl);

  const handleOpen = () => {
    setAnchorEl(ref.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
    apiRef.current.stopCellEditMode({ id, field });
  };

  const handleSelect = (department: IHierarchicalDepartmentDTO) => {
    onDepartmentChange(department);
    handleClose();
  };

  useLayoutEffect(() => {
    setAnchorEl(ref.current);
  }, []);

  return (
    <>
      <Stack
        ref={ref}
        direction="row"
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
        padding="0 8px"
        onClick={handleOpen}
      >
        {label ? (
          <Typography fontSize="0.875rem" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" title={label}>
            {label}
          </Typography>
        ) : (
          <ChevronDownIcon color={theme.palette.text.secondary} style={{ marginLeft: 'auto' }} />
        )}
      </Stack>
      <DepartmentPopover
        anchorEl={anchorEl}
        open={open}
        selected={department}
        onSelect={handleSelect}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { width: 250, borderRadius: '8px' } },
        }}
      />
    </>
  );
};
